import { useEffect, useState } from "react";
import { Space, Button, Table, Typography, Flex, Modal, Input } from "antd";
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined, CopyOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AddEditRole from "./AddEditRole";
import { deleteRole, getPermissions, getRoles } from "../../../../services/adminServices";
import { showLoader } from "../../../../utils/showloader";
import { setFormType } from "../../../../redux/slices/roleUserSlice";
import { useDispatch, useSelector } from "react-redux";
import TableComponent from "../../../space/components/table/Index";

const RoleManagement = (props) => {
    const { submitRole, saveRole, setSubmitRole } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [showForm, setShowForm] = useState(false)
    const [roles, setRoles] = useState([])
    const [permissions, setPermissions] = useState([])
    const [selectedRole, setSelectedRole] = useState(null)
    const [showSearch, setShowSearch] = useState(false)
    const [searchText, setSearchText] = useState("")
    const [showEntitlementsModal, setShowEntitlementsModal] = useState(false)
    const [entitlementsToDisplay, setEntitlementsToDisplay] = useState([])
    const [entitlementsRoleName, setEntitlementsRoleName] = useState("")

    const { formType } = useSelector(state => state.roleUser);

    useEffect(() => {
        if (formType === "role") {
            setShowForm(true)
        } else {
            setShowForm(false)
        }
    }, [formType])


    const fetchRoles = async () => {
        showLoader(true)
        let data = await getRoles();
        showLoader(false)
        setRoles(data);
    }

    const fetchPermissions = async () => {
        let data = await getPermissions();
        setPermissions(data);
    }

    useEffect(() => {
        fetchRoles();
        fetchPermissions();
    }, [])

    useEffect(() => {
        if (!props.isEdit) {
            setSelectedRole(null)
        }
    }, [props.isEdit])

    const handleAddRole = () => {
        setSelectedRole(null)
        dispatch(setFormType('role'))
        setShowForm(true)
    }


    const handleEditRole = (record) => {
        dispatch(setFormType('role'))
        setSelectedRole({ ...record })
        props.setIsEdit(true)
    }

    const handleDeleteRole = async (record) => {
        showLoader(true)
        let data = await deleteRole(record, formType)
        if (data?.status === 200) {
            fetchRoles();
        }
        showLoader(false)
    }

    const confirmRoleDelete = (record) => {
        Modal.confirm({
            title: t("Confirm"),
            content: t("Deleting the role will affect the spaces and workflows using this role. Are you sure to proceed?"),
            okText: t("Yes"),
            cancelText: t("No"),
            onOk: () => {
                handleDeleteRole(record)
                Modal.destroyAll()
            },
            footer: (_, { OkBtn, CancelBtn }) => (
                <>
                    <CancelBtn />
                    <OkBtn />
                </>
            ),
        });
    }

    const handleCopyRole = (record) => {
        const copiedRole = {
            ...record,
            role: `${t("Copy of")} ${record.role}`,
            isDefault: false
        }
        delete copiedRole.roleId
        delete copiedRole.createdOn
        delete copiedRole.createdBy
        setSelectedRole(copiedRole)
        dispatch(setFormType('role'))
        setShowForm(true)
    }

    const handleEntitlementClick = (record) => {
        const entitlements = record?.entitlements || []
        const matchedPermissions = permissions.filter(permission => entitlements.includes(permission.name))
        setEntitlementsToDisplay(matchedPermissions)
        setEntitlementsRoleName(record?.role || "")
        setShowEntitlementsModal(true)
    }

    const handleEntitlementsModalClose = () => {
        setShowEntitlementsModal(false)
        setEntitlementsToDisplay([])
        setEntitlementsRoleName("")
    }

    const entitlementsModalColumns = [
        {
            title: t("Permission"),
            dataIndex: "name",
            key: "name",
        },
        {
            title: t("Description"),
            dataIndex: "description",
            key: "description",
        },
    ]

    const reloadTable = () => {
        fetchRoles();
    }

    const handleSearchToggle = () => {
        setShowSearch(!showSearch)
        if (showSearch) {
            setSearchText("")
        }
    }

    const filteredRoles = roles.filter(role => {
        if (!searchText) return true;
        const searchLower = searchText.toLowerCase();
        return (
            role.role?.toLowerCase().includes(searchLower) ||
            role.description?.toLowerCase().includes(searchLower)
        );
    });

    const tableConfig = {
        type: "table",
        id: "entitlements_table",
        selectable: false,
        columns: [
            {
                title: t("Role"),
                dataIndex: "role",
                key: "role",
                width: "20%"
            },
            {
                title: t("Assign"),
                dataIndex: "entitlements",
                key: "entitlements",
                width: "20%",
                render: (text, record, rowIndex) => {
                    return (
                        <span className="cursor-pointer" style={{ color: "#0958d9", fontWeight: 600 }} onClick={() => handleEntitlementClick(record)}>{text?.length || 0} {t("Permissions")}</span>
                    )
                }
            },
            {
                title: t("Description"),
                dataIndex: "description",
                key: "Description",
                width: "40%",
            },
            {
                title: t('Action'),
                dataIndex: "",
                width: "10%",
                render: (text, record, rowIndex) => {
                    return (
                        <Space>
                            <Button
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => handleCopyRole(record)}
                                title={t("Copy Role")}
                            />
                            {
                                !record.isDefault ?
                                    <Button
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={() => handleEditRole(record, rowIndex)}
                                    />
                                    : null
                            }
                            {
                                !record.isDefault ?
                                    <Button
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        onClick={() => confirmRoleDelete(record)}
                                    />
                                    : null
                            }

                        </Space>
                    )
                },
            }
        ]
    }

    const locale = {
        emptyText: (
            <span>
                <p style={{ fontSize: "1rem", fontWeight: "600", color: "#000", marginBottom :"16px" }}>{t("Add new user role to manage the user data")}</p>
                <Button size="large" className="submit-button" icon={<PlusOutlined />} onClick={handleAddRole}>{t("New")}</Button>
            </span>
        ),
    };


    return (
        <div className="tab-content">
            {
                !showForm ?
                    <>
                        <Flex align="center" justify="space-between">
                            <Typography.Title level={4} style={{ margin: 0, padding: 0 }}>{t("Role Management")}</Typography.Title>
                            <Flex gap={8} align="center" justify="flex-end">
                                {showSearch && (
                                    <Input
                                        placeholder={t("Search roles...")}
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        style={{ width: 250 }}
                                        allowClear
                                        size="large"
                                    />
                                )}
                                <Button
                                    size="large"
                                    icon={showSearch ? <CloseOutlined /> : <SearchOutlined />}
                                    onClick={handleSearchToggle}
                                    type={"default"}
                                />
                                <Button size="large" className="submit-button" icon={<PlusOutlined />} onClick={handleAddRole}>{t('Add Role')}</Button>
                            </Flex>
                        </Flex>
                        <div className="table-content">
                            <TableComponent config={tableConfig} className="white-header-table" locale={locale} data={filteredRoles} pagination={{ position: ["bottomLeft"] }} />
                        </div>

                    </>
                    : <AddEditRole selectedRole={selectedRole} reloadTable={reloadTable} permissions={permissions} setShowForm={setShowForm} submitRole={submitRole} saveRole={saveRole} setSubmitRole={setSubmitRole} />

            }
            <Modal
                title={entitlementsRoleName ? `${t("Permissions")} - ${entitlementsRoleName}` : t("Permissions")}
                open={showEntitlementsModal}
                onCancel={handleEntitlementsModalClose}
                footer={[
                    <Button key="close" onClick={handleEntitlementsModalClose}>{t("Close")}</Button>
                ]}
                className="form-editor-preview"
                width={700}
                styles={{ body: {  maxHeight: "82vh", overflowY: "auto" } }}
            >
                <Table
                    rowKey="name"
                    columns={entitlementsModalColumns}
                    dataSource={entitlementsToDisplay}
                    pagination={{ position: ["bottomLeft"], pageSize: 10 }}
                    locale={{ emptyText: t("No permissions assigned") }}
                />
            </Modal>
        </div>
    )
}

export default RoleManagement
